import { customAuth } from "../standardAuthPatterns";
import { caCertificateItem, defineConnector } from "../types";
import logo from "../apiKey/logo.svg";

export const vapid = defineConnector({
  identifier: "vapid",
  name: "VAPID",
  tagLine: "Authenticate Web Push requests",
  logo,
  tags: ["custom"],
  keywords:
    "web push notifications voluntary application server identification rfc 8292",
  protocol: "http",
  defaultUrlPattern: () =>
    "^https://(fcm\\.googleapis\\.com|updates\\.push\\.services\\.mozilla\\.com|[a-z0-9-]+\\.push\\.apple\\.com|[a-z0-9-]+\\.notify\\.windows\\.com)/",
  customizeStepHandledInline: true,
  auth: (app) =>
    customAuth("vapid", {
      subject: app.config.subject,
      privateKey: app.secret.privateKey,
    }),
  steps: [
    {
      title: "Configure your VAPID connector",
      items: [
        {
          kind: "input",
          label: "Name",
          defaultValue: "VAPID",
          target: { kind: "connectedApp", field: "name" },
        },
        {
          kind: "url-pattern",
          placeholder: "updates.push.services.mozilla.com",
          target: { kind: "connectedApp", field: "urlPattern" },
        },
        {
          kind: "markdown",
          content:
            "Use the signing key pair registered with your push subscriptions. The default URL pattern covers Google, Mozilla, Apple, and Windows push services. For other push services, including self-hosted services, edit the URL pattern to include their HTTPS endpoints. This connector adds [VAPID authentication](https://www.rfc-editor.org/rfc/rfc8292.html) to HTTPS requests. Your workflow must provide the Web Push headers and encrypt message payloads separately.",
        },
        {
          kind: "input",
          label: "Subject",
          placeholder: "mailto:push@example.com",
          helperText: "A contact URI using mailto: or HTTPS.",
          required: true,
          target: { kind: "configuration", field: "subject" },
        },
        {
          kind: "input",
          label: "Private key",
          generate: { type: "p256-private-key", publicKeyField: "publicKey" },
          helperText:
            "Your base64url-encoded, 32-byte P-256 VAPID private key. The public key is derived automatically.",
          required: true,
          target: { kind: "secrets", field: "privateKey" },
        },
        {
          kind: "copyable",
          label: "Public key (applicationServerKey)",
          value: ({ publicKey }: Record<string, string>) => publicKey ?? "",
          visibleWhen: [
            { target: { kind: "configuration", field: "publicKey" } },
          ],
        },
      ],
    },
    {
      title: "Verify the connection (optional)",
      items: [
        caCertificateItem,
        {
          kind: "test-url",
          label: "Test URL",
          placeholder: "https://updates.push.services.mozilla.com/health",
          helperText:
            "Use a read-only endpoint covered by the URL pattern that accepts VAPID authentication. Leave blank if your push service has no suitable test endpoint.",
          urlTarget: { kind: "configuration", field: "testUrl" },
          methodTarget: { kind: "configuration", field: "testMethod" },
        },
      ],
    },
  ],
  connectionTest: {
    protocol: "http",
    request: ({ testUrl, testMethod }) => ({
      url: testUrl ?? "",
      method: testMethod === "POST" ? "POST" : "GET",
    }),
  },
  usageHint: ({ publicKey }: Record<string, string>) =>
    "This connector adds an ES256 VAPID Authorization header with the request origin as its audience and a 12-hour expiry. Provide Web Push headers and encrypted payloads in your workflow." +
    (publicKey
      ? ` The VAPID public key is ${publicKey}. Use it as applicationServerKey when calling PushManager.subscribe in the browser; it is public and can be included in client-side code.`
      : ""),
});
